import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import {
  isMobileSafari,
  isAndroid,
  isMobile,
  isSafari,
} from "react-device-detect";
import OperatingSystemInfoProvider from "utils/deviceDetect";
import { useMobileDimension } from "utils/hooks";
import { debounce } from "throttle-debounce";
import { useTranslation } from "react-i18next";
import { deviceDisableReason } from "utils/constants";
import logger from "utils/logger";
import Spinner from "components/Spinner";
import Video from "components/Video";
import { test } from "utils/helpers";
import useBackgroundEffect from "utils/useBackgroundEffect";
import "./SelfView.scss";

const prefix = "SELFVIEW:";
const isMobileOrTablet =
  isMobile || OperatingSystemInfoProvider.IsTabletDevice();
// same constrains tha VC use when it gets user media
const DEFAULT_VIDEO_CONSTRAINTS = {
  height: { ideal: isMobileOrTablet ? 360 : 720 },
  width: { ideal: isMobileOrTablet ? 640 : 1280 },
  frameRate: { ideal: 30 },
};
let originalStream = null;

const mapStateToProps = ({ devices, config }) => ({
  selectedCamera: devices.selectedCamera,
  mirrorSelfView: devices.mirrorSelfView,
  isCameraTurnedOn: devices.isCameraTurnedOn,
  isCameraDisabled: devices.isCameraDisabled,
  cameraDisableReasons: devices.cameraDisableReasons,
  cameraMuteControlToggle: config.urlCameraMuteControl.value,
});

const SelfView = ({
  mirrorSelfView,
  ignoreMuteState,
  externalControls,
  selectedCamera,
  isCameraTurnedOn,
  isCameraDisabled,
  cameraDisableReasons,
  cameraMuteControlToggle,
}) => {
  const { t } = useTranslation();
  const [isMobileDimension] = useMobileDimension();
  const [isPlaying, setPlaying] = useState(false);
  const [facingMode, setFacingMode] = useState("");
  const [stream, setStream] = useState(null);
  const isMounted = useRef(false);
  const pendingStreamRequest = useRef(null);
  const lastCameraId = useRef(null);
  const [backgroundEffectAvailable] = useBackgroundEffect();

  const isCameraOff = !ignoreMuteState && !isCameraTurnedOn;
  const isNotPermitted = cameraDisableReasons.includes(
    deviceDisableReason.NO_PERMISSION
  );

  const startSource = debounce(500, (retry) => {
    stopSource();
    if (selectedCamera) {
      const deviceId = selectedCamera.id;
      if (window.banuba?.getLastStream && ignoreMuteState) {
        const stream = window.banuba.getLastStream();
        logger.info(`${prefix} banuba.getLastStream active=${stream?.active}`);
        if (stream?.active) {
          stream.fromCache = true;
          setStream(stream);
          stream.getVideoTracks().forEach((track) => {
            setFacingMode(track.getSettings().facingMode || "desktop");
          });
          return;
        }
      }
      logger.info(`${prefix} getUserMedia start, banuba available=${!!window.banuba?.effectBackground}`);
      const streamPromise = navigator.mediaDevices.getUserMedia({
        video: { deviceId, ...DEFAULT_VIDEO_CONSTRAINTS },
      });

      pendingStreamRequest.current = streamPromise;

      streamPromise
        .then((stream) => {
          // Check if this request is still the current one
          if (pendingStreamRequest.current !== streamPromise) {
            stream.getVideoTracks().forEach((track) => track.stop());
            return;
          }

          if (isMounted.current) {
            logger.info(`${prefix} Stream started for device ${deviceId}`);
            stream.getVideoTracks().forEach((track) => {
              setFacingMode(track.getSettings().facingMode || "desktop");
            });
            if (window.banuba?.effectBackground && !stream.fromCache) {
              logger.info(`${prefix} applying banuba effectBackground`);
              window.banuba
                .effectBackground(stream, true)
                .then((effectStream) => {
                  originalStream = stream;
                  logger.info(`${prefix} banuba effectBackground resolved, isSafari=${isSafari}`);
                  if (isSafari) {
                    setTimeout(() => {
                      // workaround, sometimes on Safari it take more time to get working stream from canvas and we need some delay berore tye to assign it to the video element
                      setStream(effectStream);
                    }, 300);
                  } else {
                    setStream(effectStream);
                  }
                })
                .catch((e) => {
                  logger.error(`${prefix} banuba effectBackground failed: ${e}`);
                  setStream(stream);
                });
            } else {
              logger.info(`${prefix} banuba not available or fromCache, setting raw stream`);
              setStream(stream);
            }
          } else {
            setTimeout(() => {
              stream.getVideoTracks().forEach((track) => track.stop());
              if (originalStream) {
                stream.getVideoTracks().forEach((track) => track.stop());
                originalStream = null;
              }
            });
          }
        })
        .catch((e) => {
          logger.error(`${prefix} getUserMedia failed: ${e.name}`);
          setStream(null);
          if (!retry && e.name === "NotReadableError") {
            startSource(true);
          }
        });
    }
  });

  const stopSource = () => {
    if (stream) {
      setStream(null);
      setPlaying(false);
      const stopTracks = () => {
        stream.getVideoTracks().forEach((track) => track.stop());
        if (originalStream) {
          stream.getVideoTracks().forEach((track) => track.stop());
          originalStream = null;
        }
      };
      if (ignoreMuteState) {
        stopTracks();
        return;
      }
      setTimeout(stopTracks);
    }
  };

  const onPlaying = () => {
    setPlaying(true);
  };

  const onPause = () => {
    setPlaying(false);
  };

  const onVideoError = (error) => {
    logger.error(`${prefix} Video error: ${error.message}, restarting stream`);
    startSource(true);
  };

  useEffect(
    () => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, // eslint-disable-next-line
    [
      /* on mount only */
    ]
  );

  useEffect(() => {
    const currentCameraId = selectedCamera?.id;
    const shouldRestart = currentCameraId !== lastCameraId.current;

    if (shouldRestart) {
      lastCameraId.current = currentCameraId;
    }

    // Restart if banuba just became available but hasn't processed the current stream yet.
    // This handles the race where getUserMedia (faster with ideal constraints) resolved before
    // the banuba WASM plugin finished loading, causing the raw stream to be shown without effect.
    const banubaReadyAndNotApplied =
      backgroundEffectAvailable &&
      window.banuba?.effectBackground &&
      !!stream &&
      !originalStream;

    if (!isCameraDisabled && isCameraTurnedOn) {
      if (selectedCamera && selectedCamera.isStarted) {
        // Only restart if camera ID actually changed, or banuba just became available
        logger.info(
          `${prefix} isCameraTurnedOn effect: shouldRestart=${shouldRestart} hasStream=${!!stream} backgroundEffectAvailable=${backgroundEffectAvailable} hasBanuba=${!!window.banuba?.effectBackground} originalStream=${!!originalStream} banubaReadyAndNotApplied=${banubaReadyAndNotApplied}`
        );
        if (shouldRestart || !stream || banubaReadyAndNotApplied) {
          startSource();
        }
      } else {
        stopSource();
      }
    } else if (!isCameraDisabled && ignoreMuteState) {
      logger.info(
        `${prefix} ignoreMuteState effect: shouldRestart=${shouldRestart} hasStream=${!!stream} backgroundEffectAvailable=${backgroundEffectAvailable} hasBanuba=${!!window.banuba?.effectBackground} originalStream=${!!originalStream} banubaReadyAndNotApplied=${banubaReadyAndNotApplied}`
      );
      if (shouldRestart || !stream || banubaReadyAndNotApplied) {
        startSource();
      }
    } else {
      stopSource();
    }
    return () => {
      startSource.cancel();
      if (stream) {
        setTimeout(() => {
          stream.getVideoTracks().forEach((track) => track.stop());
          if (originalStream) {
            stream.getVideoTracks().forEach((track) => track.stop());
            originalStream = null;
          }
        });
      }
    };
    // eslint-disable-next-line
  }, [
    ignoreMuteState,
    isCameraDisabled,
    isCameraTurnedOn,
    selectedCamera,
    backgroundEffectAvailable,
  ]);

  const Loading = () => {
    if (!isCameraOff && !isPlaying) {
      return <Spinner width={24} height={24} />;
    }
    return null;
  };

  const ExternalControls = () => {
    if (!cameraMuteControlToggle) {
      return null;
    }

    if (
      (isMobileSafari || isAndroid || isMobileDimension) &&
      externalControls
    ) {
      return (
        <div className="external-controls-container">{externalControls}</div>
      );
    }
    return null;
  };

  if (isCameraOff || isNotPermitted) {
    return (
      <div className="self-view">
        <div className="placeholder" {...test("SELF_VIEW_OFF")}>
          {t("CAMERA_OFF")}
        </div>
      </div>
    );
  }

  return (
    <div className="self-view">
      <div className="self-view-render">
        <Loading />
        <Video
          stream={stream}
          data-playing={isPlaying}
          className={`${
            !mirrorSelfView ? "stop-mirror-self-view " : ""
          }video-tile ${facingMode}`}
          onPlaying={onPlaying}
          onPause={onPause}
          onError={onVideoError}
          {...test("SELF_VIEW")}
        />
        <ExternalControls />
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(SelfView);
